// Shared interpretation of API status values, so the dashboard, topbar and
// billing pages agree on what "open" or "needs attention" means.
import { parseApiDate } from './format.js';
import { t } from './i18n.jsx';

// PaymentStatus: pending | paid | failed | cancelled
// An invoice of €0 has nothing to pay, so it is never open (nor overdue).
export function isOpenInvoice(inv) {
  if (!(Number(inv?.total_amount) > 0)) return false;
  return inv?.status === 'pending' || inv?.status === 'failed';
}

export function isOverdueInvoice(inv) {
  if (!isOpenInvoice(inv)) return false;
  const due = parseApiDate(inv.due_date);
  return !!due && due.getTime() < Date.now();
}

// HostingStatus: active | suspended | terminated | pending | pending_cancellation
// An active hosting also needs attention when its health check fails.
export function hostingNeedsAttention(h) {
  if (h?.status === 'suspended') return true;
  return h?.status === 'active' && (h.health_status === 'down' || h.health_status === 'degraded');
}

// Display status for an invoice: a pending invoice past its due date is "overdue".
export function invoiceDisplayStatus(inv) {
  if (isOverdueInvoice(inv)) return 'overdue';
  if (inv?.status === 'pending') return 'open';
  return inv?.status || 'unknown';
}

export function invoiceStatusClass(inv) {
  const s = invoiceDisplayStatus(inv);
  if (s === 'paid') return '';
  if (s === 'overdue' || s === 'failed') return 'is-danger';
  if (s === 'cancelled') return 'is-muted';
  return 'is-warn';
}

export function invoiceStatusLabel(inv, lang) {
  const s = invoiceDisplayStatus(inv);
  return INVOICE_STATUSES.includes(s) ? t(lang, `invoiceStatus.${s}`) : s;
}

const INVOICE_STATUSES = ['paid', 'open', 'overdue', 'failed', 'cancelled'];
const HOSTING_STATUSES = ['active', 'suspended', 'terminated', 'pending', 'pending_cancellation'];

export function hostingStatusLabel(status, lang) {
  return HOSTING_STATUSES.includes(status) ? t(lang, `hostingStatus.${status}`) : status || '—';
}

// What to show for a hosting: the lifecycle status wins unless the hosting is
// active, in which case the runtime health check decides.
// HealthCheckStatus: healthy | degraded | down | unknown
// Returns { key, tone: '' | 'is-warn' | 'is-danger' | 'is-muted', label, message }.
export function hostingState(h, lang) {
  const status = h?.status;
  if (status !== 'active') {
    const tone = status === 'terminated' ? 'is-danger' : 'is-warn';
    return { key: status || 'unknown', tone, label: hostingStatusLabel(status, lang), message: null };
  }
  const health = h?.health_status || 'unknown';
  const message = h?.health_message || null;
  if (health === 'healthy') return { key: 'healthy', tone: '', label: t(lang, 'health.healthy'), message };
  if (health === 'degraded') return { key: 'degraded', tone: 'is-warn', label: t(lang, 'health.degraded'), message };
  if (health === 'down') return { key: 'down', tone: 'is-danger', label: t(lang, 'health.down'), message };
  return { key: 'unchecked', tone: 'is-muted', label: t(lang, 'health.unknown'), message };
}
